// src/lib/api_elevenlabs.ts
// Typed client for the ElevenLabs Audio AI module (Track E10).
// Backend: apps/elevenlabs/ — mounted at /admin/elevenlabs with admin_auth JWT.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function djangoUpload<T = any>(path: string, formData: FormData): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail || `Upload failed: ${res.status}`, res.status);
  }
  return res.json();
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

const BASE = '/api/v1/admin/elevenlabs';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface ELVoice {
  voice_id: string;
  name: string;
  category: 'premade' | 'cloned' | 'generated';
  language?: string;
  preview_url?: string;
}

export interface ELModel {
  model_id: string;
  name: string;
  description?: string;
  languages?: string[];
}

export type ELJobType = 'tts' | 'podcast' | 'sfx' | 'music' | 'stt' | 'isolate' | 'dub';
export type ELJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface ELJob {
  id: string;
  org_id: string;
  job_type: ELJobType;
  status: ELJobStatus;
  audio_url: string | null;
  duration: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
}

export interface Paginated<T> {
  items: T[];
  count: number;
}

// ═══════════════════════════════════════════════════════════════
// Reads (GET)
// ═══════════════════════════════════════════════════════════════

export function listVoices(): Promise<ELVoice[]> {
  return djangoFetch(`${BASE}/voices/`);
}

export function listModels(): Promise<ELModel[]> {
  return djangoFetch(`${BASE}/models/`);
}

export function listJobs(params?: {
  job_type?: ELJobType;
  status?: ELJobStatus;
  limit?: number;
  offset?: number;
}): Promise<Paginated<ELJob>> {
  return djangoFetch(`${BASE}/jobs/${qs(params || {})}`);
}

// ═══════════════════════════════════════════════════════════════
// Sync generation (response = immediate result)
// ═══════════════════════════════════════════════════════════════

export interface TTSIn {
  text: string;
  voice_id: string;
  model_id?: string;
  stability?: number;
  similarity_boost?: number;
  style?: number;
  speed?: number;
}

export interface TTSOut {
  audio_url: string;
  duration: number;
}

export function textToSpeech(data: TTSIn): Promise<TTSOut> {
  return djangoFetch(`${BASE}/tts/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface SFXIn {
  text: string;
  duration_seconds?: number;
}

export function generateSFX(data: SFXIn): Promise<{ audio_url: string; duration: number }> {
  return djangoFetch(`${BASE}/sfx/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface MusicIn {
  prompt: string;
  duration_seconds?: number;
  instrumental?: boolean;
}

export function generateMusic(data: MusicIn): Promise<{ audio_url: string; duration: number }> {
  return djangoFetch(`${BASE}/music/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function isolateAudio(file: File): Promise<{ audio_url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  return djangoUpload(`${BASE}/isolate/`, fd);
}

// ═══════════════════════════════════════════════════════════════
// Async generation — return job, poll for completion
// ═══════════════════════════════════════════════════════════════

export interface PodcastIn {
  mode: 'conversation' | 'bulletin';
  source_type: 'url' | 'text';
  source: string;
  voice_ids: string[];
}

export function generatePodcast(data: PodcastIn): Promise<ELJob> {
  return djangoFetch(`${BASE}/podcast/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface DubIn {
  source_url: string;
  target_language: string;
}

export function dubAudio(data: DubIn): Promise<ELJob> {
  return djangoFetch(`${BASE}/dub/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════════
// STT (upload + sync transcription)
// ═══════════════════════════════════════════════════════════════

export interface STTOut {
  text: string;
  segments?: { start: number; end: number; text: string; speaker?: string }[];
}

export function speechToText(file: File, diarize?: boolean): Promise<STTOut> {
  const fd = new FormData();
  fd.append('file', file);
  if (diarize) fd.append('diarize', 'true');
  return djangoUpload(`${BASE}/stt/`, fd);
}

// ═══════════════════════════════════════════════════════════════
// Job polling
// ═══════════════════════════════════════════════════════════════

export function checkJob(jobId: string): Promise<ELJob> {
  return djangoFetch(`${BASE}/jobs/${encodeURIComponent(jobId)}/check/`, {
    method: 'POST',
  });
}
